import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';

import { DefaultSortSpec, type SortSpec } from '@/domain/shared/sortOptions';
import type { IHomeSystem } from '@/domain/entities/system';
import type { SystemsState } from './systemsSliceTypes';
import {
  createPropertySystem,
  deletePropertySystem,
  fetchPropertySystemById,
  fetchPropertySystemsByPropertyId,
  updatePropertySystem,
} from './systemsThunks';

const initialState: SystemsState = {
  itemsByPropertyID: {},
  dirtyByPropertyID: {},
  hasFetchedByPropertyID: {},
  isListingByPropertyID: {},
  errorByPropertyID: {},

  selected: null,
  isLoadingOne: false,
  isSaving: false,
  isDeleting: false,
  hasChanges: false,

  activeSort: DefaultSortSpec,
};

const ensureBucket = (state: SystemsState, propertyID: string) => {
  if (!state.itemsByPropertyID[propertyID]) state.itemsByPropertyID[propertyID] = [];
  if (state.hasFetchedByPropertyID[propertyID] === undefined) state.hasFetchedByPropertyID[propertyID] = false;
  if (state.isListingByPropertyID[propertyID] === undefined) state.isListingByPropertyID[propertyID] = false;
  if (state.errorByPropertyID[propertyID] === undefined) state.errorByPropertyID[propertyID] = null;
  if (!state.dirtyByPropertyID[propertyID]) state.dirtyByPropertyID[propertyID] = {};
};

const upsertById = (items: IHomeSystem[], rec: IHomeSystem): IHomeSystem[] => {
  const id = rec.id;
  if (!id) return [rec, ...items]; // fallback safe
  const idx = items.findIndex((x) => x.id === id);
  if (idx === -1) return [rec, ...items];
  const copy = items.slice();
  copy[idx] = rec;
  return copy;
};

const removeById = (items: IHomeSystem[], id: string): IHomeSystem[] => items.filter((x) => x.id !== id);

export const systemsSlice = createSlice({
  name: 'propertySystems',
  initialState,
  reducers: {
    // ---------- sort ----------
    setPropertySystemsActiveSort: (state, action: PayloadAction<SortSpec>) => {
      state.activeSort = action.payload;
    },

    // ---------- editor ----------
    clearSelectedPropertySystem: (state) => {
      state.selected = null;
      state.hasChanges = false;
    },

    patchSelectedPropertySystem: (state, action: PayloadAction<Partial<IHomeSystem>>) => {
      if (!state.selected) return;

      const updated: IHomeSystem = {
        ...state.selected,
        ...action.payload,
      };

      state.selected = updated;
      state.hasChanges = true;

      const propertyID = updated.propertyID;
      if (propertyID) {
        ensureBucket(state, propertyID);
        state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], updated);
      }
    },

    markPropertySystemsClean: (state) => {
      state.hasChanges = false;
    },

    // ---------- list/editor helpers (per-item dirty) ----------
    upsertPropertySystemLocal: (state, action: PayloadAction<{ propertyID: string; item: IHomeSystem }>) => {
      const { propertyID, item } = action.payload;
      ensureBucket(state, propertyID);

      state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], item);

      if (item.id) {
        state.dirtyByPropertyID[propertyID][item.id] = true;
      }
    },

    markPropertySystemDirty: (state, action: PayloadAction<{ propertyID: string; systemID: string }>) => {
      const { propertyID, systemID } = action.payload;
      if (!state.dirtyByPropertyID[propertyID]) state.dirtyByPropertyID[propertyID] = {};
      state.dirtyByPropertyID[propertyID][systemID] = true;
    },

    markPropertySystemClean: (state, action: PayloadAction<{ propertyID: string; id: string }>) => {
      const { propertyID, id } = action.payload;
      ensureBucket(state, propertyID);
      state.dirtyByPropertyID[propertyID][id] = false;
    },

    clearPropertySystemsDirtyForProperty: (state, action: PayloadAction<{ propertyID: string }>) => {
      const { propertyID } = action.payload;
      ensureBucket(state, propertyID);
      state.dirtyByPropertyID[propertyID] = {};
    },

    setPropertySystemsForProperty: (state, action: PayloadAction<{ propertyID: string; items: IHomeSystem[] }>) => {
      const { propertyID, items } = action.payload;
      ensureBucket(state, propertyID);
      state.itemsByPropertyID[propertyID] = items;
      state.hasFetchedByPropertyID[propertyID] = true;
      state.errorByPropertyID[propertyID] = null;
      // (optional) do not clear dirty automatically — you decide
      // state.dirtyByPropertyID[propertyID] = {};
    },
  },

  extraReducers: (builder) => {
    builder
      // ---------------- list by property ----------------
      .addCase(fetchPropertySystemsByPropertyId.pending, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);
        state.isListingByPropertyID[propertyID] = true;
        state.errorByPropertyID[propertyID] = null;
      })
      .addCase(fetchPropertySystemsByPropertyId.fulfilled, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);

        state.isListingByPropertyID[propertyID] = false;
        state.itemsByPropertyID[propertyID] = action.payload;
        state.hasFetchedByPropertyID[propertyID] = true;
      })
      .addCase(fetchPropertySystemsByPropertyId.rejected, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);

        state.isListingByPropertyID[propertyID] = false;
        state.hasFetchedByPropertyID[propertyID] = true;
        state.errorByPropertyID[propertyID] =
          (action.payload as { message?: string } | undefined)?.message ?? action.error.message ?? 'Failed to fetch';
      })

      // ---------------- get one ----------------
      .addCase(fetchPropertySystemById.pending, (state) => {
        state.isLoadingOne = true;
      })
      .addCase(fetchPropertySystemById.fulfilled, (state, action) => {
        state.isLoadingOne = false;

        const rec = action.payload;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
        }
      })
      .addCase(fetchPropertySystemById.rejected, (state) => {
        state.isLoadingOne = false;
      })

      // ---------------- create ----------------
      .addCase(createPropertySystem.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createPropertySystem.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload.data as IHomeSystem;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (!propertyID) return;

        ensureBucket(state, propertyID);

        const tempId = action.meta.arg.tempId;

        if (tempId) {
          const idx = state.itemsByPropertyID[propertyID].findIndex((x) => x.id === tempId);

          if (idx !== -1) {
            // ✅ replace temp in-place (no duplication)
            state.itemsByPropertyID[propertyID][idx] = rec;

            // ✅ cleanup temp dirty entry
            delete state.dirtyByPropertyID[propertyID][tempId];
          } else {
            // fallback
            state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
          }
        } else {
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
        }

        state.hasFetchedByPropertyID[propertyID] = true;

        // ✅ mark real id as clean
        if (rec.id) state.dirtyByPropertyID[propertyID][rec.id] = false;
      })
      .addCase(createPropertySystem.rejected, (state) => {
        state.isSaving = false;
      })

      // ---------------- update ----------------
      .addCase(updatePropertySystem.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updatePropertySystem.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
          if (rec.id) state.dirtyByPropertyID[propertyID][rec.id] = false;
        }
      })
      .addCase(updatePropertySystem.rejected, (state) => {
        state.isSaving = false;
      })

      // ---------------- delete ----------------
      .addCase(deletePropertySystem.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deletePropertySystem.fulfilled, (state, action) => {
        state.isDeleting = false;

        const { id, propertyID } = action.meta.arg;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = removeById(state.itemsByPropertyID[propertyID], id);
          delete state.dirtyByPropertyID[propertyID][id];
        }

        if (state.selected?.id === id) {
          state.selected = null;
          state.hasChanges = false;
        }
      })
      .addCase(deletePropertySystem.rejected, (state) => {
        state.isDeleting = false;
      });
  },
});

export const {
  setPropertySystemsActiveSort,
  clearSelectedPropertySystem,
  patchSelectedPropertySystem,
  markPropertySystemsClean,
  setPropertySystemsForProperty,
  upsertPropertySystemLocal,
  markPropertySystemDirty,
  markPropertySystemClean,
  clearPropertySystemsDirtyForProperty,
} = systemsSlice.actions;

export default systemsSlice.reducer;

/* ---------------- selectors ---------------- */

const selectSystemsState = (state: RootState) => state.propertySystems;

export const selectPropertySystemsActiveSort = createSelector([selectSystemsState], (s) => s.activeSort);

export const selectPropertySystemsByPropertyId = createSelector(
  [selectSystemsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return [];
    return s.itemsByPropertyID[propertyID] ?? [];
  },
);

export const selectPropertySystemsListingByPropertyId = createSelector(
  [selectSystemsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return false;
    return s.isListingByPropertyID[propertyID] ?? false;
  },
);

export const selectPropertySystemsFetchedByPropertyId = createSelector(
  [selectSystemsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return false;
    return s.hasFetchedByPropertyID[propertyID] ?? false;
  },
);

export const selectPropertySystemsDirtyByPropertyId = createSelector(
  [selectSystemsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return {};
    return s.dirtyByPropertyID[propertyID] ?? {};
  },
);

export const selectPropertySystemIsDirty = createSelector(
  [selectSystemsState, (_: RootState, propertyID?: string | null, id?: string | null) => ({ propertyID, id })],
  (s, { propertyID, id }) => {
    if (!propertyID || !id) return false;
    return s.dirtyByPropertyID[propertyID]?.[id] ?? false;
  },
);

export const selectPropertySystemsDirtyIds = createSelector(
  [selectSystemsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return [];
    const map = s.dirtyByPropertyID[propertyID] ?? {};
    return Object.keys(map).filter((id) => map[id]);
  },
);
